/**
 * useOfflineReviewQueue Hook
 * 
 * Provides manager/admin access to review, retry, and manage offline queue items.
 * Exposes filtering, manager annotations, and bulk actions.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  listQueueItems, 
  getQueueStats, 
  updateQueueItem,
  retryQueueItem,
  discardQueueItem,
  clearCompletedQueueItems,
  replayPendingQueue
} from '@/lib/offline';
import { isBackofficeAction, getBackofficeModule, getBackofficeActionDisplayName } from '@/lib/offline/backofficeDedupe';

// Manager annotation statuses
export const REVIEW_STATUS = {
  NONE: 'none',
  REVIEWED: 'reviewed',
  DISMISSED: 'dismissed',
  NEEDS_FOLLOWUP: 'needs_followup'
};

/**
 * useOfflineReviewQueue Hook
 */
export function useOfflineReviewQueue(options = {}) {
  const { autoRefresh = true, refreshInterval = 5000 } = options;
  
  // State
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    syncing: 0,
    completed: 0,
    failed: 0,
    conflict: 0
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: null,        // pending, failed, conflict, completed
    module: null,        // Settings, Staff, Menu, etc.
    actionType: null,    // BUSINESS_PROFILE_UPDATE, etc.
    dateFrom: null,
    dateTo: null,
    reviewStatus: null   // none, reviewed, dismissed, needs_followup
  });

  // Refresh data from IndexedDB
  const refreshData = useCallback(async () => {
    try {
      const [allItems, currentStats] = await Promise.all([
        listQueueItems(),
        getQueueStats()
      ]);
      
      // Enrich items with module info
      const enrichedItems = allItems.map(item => ({
        ...item,
        moduleName: getBackofficeModule(item.type),
        actionDisplayName: getBackofficeActionDisplayName(item.type),
        isBackoffice: isBackofficeAction(item)
      }));
      
      setItems(enrichedItems);
      setStats(currentStats);
    } catch (error) {
      console.error('[useOfflineReviewQueue] Failed to refresh:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and auto-refresh
  useEffect(() => {
    refreshData();
    
    if (autoRefresh) {
      const interval = setInterval(refreshData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshData, autoRefresh, refreshInterval]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Status filter
      if (filters.status && item.status !== filters.status) {
        return false;
      }
      
      // Module filter
      if (filters.module && getBackofficeModule(item.type) !== filters.module) {
        return false;
      }
      
      // Action type filter
      if (filters.actionType && item.type !== filters.actionType) {
        return false;
      }
      
      // Date range filter
      if (filters.dateFrom) {
        const itemDate = new Date(item.createdAt);
        if (itemDate < new Date(filters.dateFrom)) {
          return false;
        }
      }
      if (filters.dateTo) {
        const itemDate = new Date(item.createdAt);
        if (itemDate > new Date(filters.dateTo)) {
          return false;
        }
      }
      
      // Review status filter
      if (filters.reviewStatus && item.manualResolutionStatus !== filters.reviewStatus) {
        return false;
      }
      
      return true;
    });
  }, [items, filters]);

  // Categorized items
  const pendingItems = useMemo(() => 
    filteredItems.filter(i => i.status === 'pending' || i.status === 'syncing'), 
    [filteredItems]
  );
  
  const failedItems = useMemo(() => 
    filteredItems.filter(i => i.status === 'failed'), 
    [filteredItems]
  );
  
  const conflictItems = useMemo(() => 
    filteredItems.filter(i => i.status === 'conflict'), 
    [filteredItems]
  );
  
  const blockedItems = useMemo(() => 
    filteredItems.filter(i => i.manualResolutionStatus === REVIEW_STATUS.NEEDS_FOLLOWUP), 
    [filteredItems]
  );
  
  const recentSyncedItems = useMemo(() => 
    filteredItems.filter(i => i.status === 'completed').slice(0, 20), 
    [filteredItems]
  );

  // Summary counts
  const summaryCounts = useMemo(() => ({
    total: filteredItems.length,
    pending: pendingItems.length,
    failed: failedItems.length,
    conflict: conflictItems.length,
    blocked: blockedItems.length,
    synced: recentSyncedItems.length,
    needsAttention: failedItems.length + conflictItems.length + blockedItems.length
  }), [filteredItems, pendingItems, failedItems, conflictItems, blockedItems, recentSyncedItems]);

  // Available modules for filtering
  const availableModules = useMemo(() => {
    const modules = new Set(items.map(i => getBackofficeModule(i.type)));
    return Array.from(modules).sort();
  }, [items]);

  // Actions

  /**
   * Retry a specific queue item
   */
  const handleRetryItem = useCallback(async (itemId) => {
    try {
      await retryQueueItem(itemId);
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Retry failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Discard a specific queue item
   */
  const handleDiscardItem = useCallback(async (itemId) => {
    try {
      await discardQueueItem(itemId);
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Discard failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Add a manager note to a queue item
   */
  const handleAddNote = useCallback(async (itemId, note, actor) => {
    try {
      await updateQueueItem(itemId, {
        managerNote: note,
        noteAddedBy: actor,
        noteAddedAt: Date.now()
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Add note failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark a queue item as reviewed
   */
  const handleMarkReviewed = useCallback(async (itemId, actor) => {
    try {
      await updateQueueItem(itemId, {
        manualResolutionStatus: REVIEW_STATUS.REVIEWED,
        reviewedBy: actor,
        reviewedAt: Date.now()
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Mark reviewed failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Mark a queue item as needs follow-up
   */
  const handleMarkNeedsFollowup = useCallback(async (itemId, actor, note) => {
    try {
      await updateQueueItem(itemId, {
        manualResolutionStatus: REVIEW_STATUS.NEEDS_FOLLOWUP,
        reviewedBy: actor,
        reviewedAt: Date.now(),
        managerNote: note || undefined
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Mark needs followup failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Dismiss a queue item (admin decided to ignore)
   */
  const handleDismissItem = useCallback(async (itemId, actor, reason) => {
    try {
      await updateQueueItem(itemId, {
        manualResolutionStatus: REVIEW_STATUS.DISMISSED,
        reviewedBy: actor,
        reviewedAt: Date.now(),
        dismissReason: reason
      });
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Dismiss failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Force sync all pending items now
   */
  const handleSyncNow = useCallback(async () => {
    try {
      const result = await replayPendingQueue({ limit: 50 });
      await refreshData();
      return { success: true, result };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Sync now failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Clear all completed items
   */
  const handleClearCompleted = useCallback(async () => {
    try {
      await clearCompletedQueueItems();
      await refreshData();
      return { success: true };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Clear completed failed:', error);
      return { success: false, error: error.message };
    }
  }, [refreshData]);

  /**
   * Retry all failed items
   */
  const handleRetryAllFailed = useCallback(async () => {
    try {
      const failedIds = failedItems.map(i => i.id);
      await Promise.all(failedIds.map(id => retryQueueItem(id)));
      await refreshData();
      return { success: true, count: failedIds.length };
    } catch (error) {
      console.error('[useOfflineReviewQueue] Retry all failed:', error);
      return { success: false, error: error.message };
    }
  }, [failedItems, refreshData]);

  return {
    // Data
    items: filteredItems,
    allItems: items,
    pendingItems,
    failedItems,
    conflictItems,
    blockedItems,
    recentSyncedItems,
    stats,
    summaryCounts,
    availableModules,
    
    // State
    loading,
    filters,
    setFilters,
    
    // Actions
    retryItem: handleRetryItem,
    discardItem: handleDiscardItem,
    addNote: handleAddNote,
    markReviewed: handleMarkReviewed,
    markNeedsFollowup: handleMarkNeedsFollowup,
    dismissItem: handleDismissItem,
    syncNow: handleSyncNow,
    clearCompleted: handleClearCompleted,
    retryAllFailed: handleRetryAllFailed,
    refreshData
  };
}

export default useOfflineReviewQueue;
